import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { getGroupById } from "@/db/groups"
import { TableType } from "@/types/base/enums"
import { Tables } from "@/supabase/types"
import { PermContentType } from "@/types/base"
import { BatchPermOptions, PermAction, PermOptions, Permissions, permOptionsKey } from '@/types/base/permissions'
import { chatCanPerform } from "@/db/chats"
import { groupCanPerform } from "@/db/groups"
import { groupRolesCanPerform } from "@/db/group-roles"
import { groupUsersCanPerform } from "@/db/group-users"
import { tenantUsersCanPerform } from "@/db/tenant-users"
import { getAidonTenant, getTenantById, isAidonAdmin, tenantCanPerform } from "@/db/tenants"
import { roleCanPerform } from "@/db/roles"
import {
	ViewData,
	getAssistantTenantById,
	getChatTenantById,
	getCollectionTenantById,
	getFileTenantById,
	getFolderTenantById,
	getGroupTenantById,
	getMcpTenantById,
	getModelTenantById,
	getPresetTenantById,
	getProfileTenantById,
	getPromptTenantById,
	getToolTenantById,
	getWorkspacesTenantById
} from "./tenant-views"
import { workspacesCanPerform } from "@/db/workspaces"
import { profileCanPerform } from '@/db/profiles'
import { providerCanPerform } from '@/db/providers'
import memoizee from "memoizee"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'

const authLogger = createIsomorphicLogger('types.authuser')

export interface Groups {
  [groupId: string]: string[]
}

export interface UserData {
  id: string
  name: string
  email: string
}

function getEnumValueByName<T extends { [key: string]: string | number }>(enumObj: T, name: string): T[keyof T] | undefined {
  return enumObj[name as keyof T];
}

export enum Role {  //order here is very important as it is ordered by increasing permissions
  login = "login",
  viewer = "viewer",
  user = "user",
  powerUser = "powerUser",
  admin = "admin",	//This is a tenant admin (can be Aidon tenant as well)
  root = "root"   	//This is the Aidon root admin
}

// Define the type for the permission check function
type CanPerformFunction = (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => Promise<boolean>;

const noCheckCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	return true
}

const sharingCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	switch (options.action) {
		case PermAction.select:
			if (options.uuid) {
				return options.uuid == 'private' || options.uuid == 'public'
			}
	}

	return false
}

const configCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.context == 'aidonAdmin') {
		return isAidonAdmin(supabase, authuser)
	}

	if (options.context == 'Folder') {
		switch (options.action) {
			case PermAction.delete:
				return authuser.isAuthorizedForRole(Role.admin)
			case PermAction.edit:
				return authuser.isAuthorizedForRole(Role.powerUser)
		}
	}

	if (options.tenant && options.action != PermAction.view && options.action != PermAction.drag) {
		if (options.action == PermAction.save && authuser.isAuthorizedForRole(Role.admin)) {
			//do nothing, keep going.
			//A tenant admin need to have save in order to assign to workspace a config element originating from
			//Aidon that they want to share further.
		}
		else {
			const userTenantId = await authuser.getTenantId(supabase)
			if (userTenantId && userTenantId != options.tenant) {
				return false
			}
		}
	}

	const tblType = mapPermContentTypeToTableType(options.type)
	if (tblType && options.uuid && await authuser.isOwner(tblType, options.uuid)) {
		return true
	}

	switch (options.action) {
		case PermAction.view:
			return authuser.isAuthorizedForRole(Role.user)
		case PermAction.delete:
			return authuser.isAuthorizedForRole(Role.admin)
		case PermAction.create:
			if (options.ws && authuser.isOwnWorkspace(options.ws)) {
				return true
			}
		//fall through
		case PermAction.drag:
		case PermAction.edit:
		case PermAction.update:
		case PermAction.select:  //covers for "Workspaces"
		case PermAction.save:
			return authuser.isAuthorizedForRole(Role.powerUser)
		default:
			return false  //for now not used, so don't allow
	}
}

const modelCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (options.context == 'provider_id' || options.context == 'IMPEXP') {
		return isAidonAdmin(supabase, authuser)
	}

	return configCanPerform(supabase, authuser, options)
}

const configRelCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.tenant && options.action != PermAction.view && options.action != PermAction.drag) {
		const userTenantId = await authuser.getTenantId(supabase)
		if (userTenantId && userTenantId != options.tenant) {
			return false
		}
	}

	const tblType = mapPermContentTypeToTableType(options.type)
	if (tblType && options.uuid && await authuser.isOwner(tblType, options.uuid)) {
		return true
	}

	// if (options.ws && authuser.isOwnWorkspace(options.ws)) {
	// 	return true
	// }

	switch (options.action) {
		case PermAction.view:
			return authuser.isAuthorizedForRole(Role.user)
		case PermAction.create:
			if (options.ws && authuser.isOwnWorkspace(options.ws)) {
				return true
			}
			//fall through
		case PermAction.drag:
		case PermAction.edit:
		case PermAction.delete:
		case PermAction.update:
		case PermAction.select:  //covers for all types
		case PermAction.save:
			return authuser.isAuthorizedForRole(Role.powerUser)
		default:
			return false  //for now not used, so don't allow
	}
}

const fetchCanPerformFunctions: Record<PermContentType, CanPerformFunction | null> = {
	assistants: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	},
	assistantCollections: async (supabase, authuser, options) => {
		return configRelCanPerform(supabase, authuser, options)
	},
  assistantFiles: async (supabase, authuser, options) => {
		return configRelCanPerform(supabase, authuser, options)
	},
  assistantTools: async (supabase, authuser, options) => {
		return configRelCanPerform(supabase, authuser, options)
	},
	chats: async (supabase, authuser, options) => {
		return chatCanPerform(supabase, authuser, options)
	},
	collections: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	},
	collectionFiles: async (supabase, authuser, options) => {
		return configRelCanPerform(supabase, authuser, options)
	},
	files: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	},
	groups: async (supabase, authuser, options) => {
		return groupCanPerform(supabase, authuser, options)
	},
	groupUsers: async (supabase, authuser, options) => {
		return groupUsersCanPerform(supabase, authuser, options)
	},
	groupRoles: async (supabase, authuser, options) => {
		return groupRolesCanPerform(supabase, authuser, options)
	},
	mcps: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	},
	models: async (supabase, authuser, options) => {
		return modelCanPerform(supabase, authuser, options)
	},
	presets: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	},
	prompts: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	},
	profiles: async (supabase, authuser, options) => {
		return profileCanPerform(supabase, authuser, options)
	},
	providers: async (supabase, authuser, options) => {
		return providerCanPerform(supabase, authuser, options)
	},
	roles: async (supabase, authuser, options) => {
		return roleCanPerform(supabase, authuser, options)
	},
	tenants: async (supabase, authuser, options) => {
		return tenantCanPerform(supabase, authuser, options)
	},
	tenantUsers: async (supabase, authuser, options) => {
		return tenantUsersCanPerform(supabase, authuser, options)
	},
	tools: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	},
	workspaces: async (supabase, authuser, options) => {
		return workspacesCanPerform(supabase, authuser, options)
	},
	sharing: async (supabase, authuser, options) => {
		return sharingCanPerform(supabase, authuser, options)
	},
	config: async (supabase, authuser, options) => {
		return configCanPerform(supabase, authuser, options)
	}
}

class Authuser {
  id: string
  name: string
  email: string
	private groups: Groups
	private permissions: string[]
	private aidonTenantId: string | null = null
	private tenantId: string | null = null
  private roles = new Set<Role>();

	constructor(data: UserData, groups: Groups, permissions: string[]) {
		this.id = data.id
    this.name = data.name
    this.email = data.email
    this.groups = groups
    this.permissions = permissions
    this.init()
  }

  private init() {
    for (const grpId in this.groups) {
      const grpObj = this.groups[grpId]
      grpObj.forEach((roleName: string) => {
        const r: Role | undefined = getEnumValueByName(Role, roleName);
        if (r !== undefined) {
          this.roles.add(r)
        }
        else {
          authLogger.info(`No enum value found for ${roleName}`)
        }
      })
    }
  }

	getCachedTenantId() : string | null {
		return this.tenantId
	}

	async getAidonTenantId(supabase: SupabaseClient<Database>): Promise<string> {
		return this.getAidonTenantIdIntern(supabase)
	}

	async getTenantId(supabase: SupabaseClient<Database>): Promise<string> {
		return this.getTenantIdIntern(supabase)
	}

	async isTenantAidon(supabase: SupabaseClient<Database>): Promise<boolean> {
		return (await getTenantById(supabase, await this.getTenantId(supabase)))?.name == 'Aidon'
	}

	isOwnWorkspace(ws: Tables<"workspaces"> | null): boolean {
		return ws?.user_id === this.id
	}

  // Method to check if the user has a specific permission
  hasPermission(permission: string): boolean {
    return this.permissions.includes(permission)
  }

	isGenerated(shareMode: string): boolean {
		return shareMode == 'system' || shareMode == 'hidden'
	}

  // Method to check if the user is in a specific group by group ID (not very useful for us)
  isInGroup(groupId: string): boolean {
    return !!this.groups[groupId]
  }

  // Method to check if the user has a specific role in a group (not very useful for us)
  hasRoleInGroup(groupId: string, role: string): boolean {
    return this.groups[groupId]?.includes(role) || false
  }

  isAuthorizedForRole(neededRole: Role): boolean {
    if (this.roles.has(neededRole)) {
      return true;
    }
    return Array.from(this.roles).some((userRole: Role) => this.checkIfIsAuthorizedFor(userRole, neededRole));
  }

	async isAuthorizedForTable(supabase: SupabaseClient<Database>, tbl:TableType, refId:string, neededRole: Role): Promise<boolean> {
		const authorized = this.isAuthorizedForRole(neededRole)

		if (!authorized) {
			return false
		}

		const viewData: ViewData = await this.getViewDataCached(tbl, refId)

		// If the tenant matches, then we can check other aspects
		const userTenantId = await this.getTenantId(supabase)

		if (viewData?.user_id == this.id) {  //own stuff is always good to go
			return true
		}

		if (viewData?.tenant_id == userTenantId) {
			//Is this content public, then you're all set, system shared would be controlled elsewhere AFAICT
			if (viewData.sharing == "public" || viewData.sharing == "system") {
				return true
			}
			if (viewData.sharing == "hidden") {
				return false
			}
			if (viewData.sharing == "private" && viewData.user_id == this.id) {
				return true
			}

			//Lastly we have a possible case of sharing by email(s) to specific users
			if (viewData?.tenant_id !== undefined) {
				return this.includesUserEmail(viewData.sharing)
			}
		}

		return false
	}

	getViewDataCached = memoizee(
		async (tbl: TableType, refId: string): Promise<ViewData> => {
			authLogger.debug(`Calling getViewData for ${tbl}/${refId}`)
			return this.getViewData(tbl, refId)
		},
		{ promise: true, maxAge: 3000, primitive: true } //3 secs
	)

	async isOwner(tbl:TableType, refId:string): Promise<boolean> {
		const viewData: ViewData = await this.getViewDataCached(tbl, refId)

		if (viewData?.user_id == this.id) {  //own stuff is always good to go
			return true
		}

		return false
	}

	async getViewData(tbl:TableType, refId:string): Promise<ViewData> {
		let viewData: ViewData

		//Select from the appropriate view and set the retrieved tenant_id
		switch(tbl) {
			case "assistant":
				viewData = await getAssistantTenantById(refId)
				break;
			case "chat":
				viewData = await getChatTenantById(refId)
				break;
			case "collection":
				viewData = await getCollectionTenantById(refId)
				break;
			case "file":
				viewData = await getFileTenantById(refId)
				break;
			case "folder":
				viewData = await getFolderTenantById(refId)
				break;
			case "group":
				viewData = await getGroupTenantById(refId)
				break;
			case "mcp":
				viewData = await getMcpTenantById(refId)
				break;
			case "model":
				viewData = await getModelTenantById(refId)
				break;
			case "preset":
				viewData = await getPresetTenantById(refId)
				break;
			case "profile":
				viewData = await getProfileTenantById(refId)
				break;
			case "prompt":
				viewData = await getPromptTenantById(refId)
				break;
			case "tool":
				viewData = await getToolTenantById(refId)
				break;
			case "workspace":
				viewData = await getWorkspacesTenantById(refId)
				break;
			default:
				throw new Error(`Invalid table type: ${tbl}`)
		}

		return viewData
	}

	private includesUserEmail(emails: string): boolean {
		//Split the comma-delimited string into an array
		const emailArray = emails.split(",").map(item => item.trim())
		const isMatched = emailArray.some(item => item === this.email)
		return isMatched
	}

  private checkIfIsAuthorizedFor(userRole: Role, neededRole: Role): boolean {
		const roles = Object.values(Role);
  	const userRoleIndex = roles.indexOf(userRole);
  	const neededRoleIndex = roles.indexOf(neededRole);
  	return userRoleIndex >= 0 && neededRoleIndex >= 0 && userRoleIndex >= neededRoleIndex;
  }

	private async getTenantIdIntern(supabase: SupabaseClient<Database>): Promise<string> {
    if (!this.tenantId) {
      const loginGroups: Array<string> = []

      for (const grpId in this.groups) {
        const grpObj = this.groups[grpId]
        const res = grpObj.filter((roleName: string) => roleName == 'login')
        if (res.length != 0) {
          loginGroups.push(grpId)
        }
      }

      if (loginGroups.length == 1) {
        const group = await getGroupById(supabase, loginGroups[0])
        this.tenantId = group.tenant_id
      }
    }
    return this.tenantId ?? ''  //empty string just in case
  }

	private async getAidonTenantIdIntern(supabase: SupabaseClient<Database>): Promise<string> {
    if (!this.aidonTenantId) {
			const aidonTenant = await getAidonTenant(supabase)
      this.aidonTenantId = aidonTenant.id
    }
    return this.aidonTenantId
  }

  toSerializable() {
    return {
      data: {
        id: this.id,
        name: this.name,
        email: this.email
      },
      groups: this.groups,
      permissions: this.permissions
    }
  }
}

export function mapPermContentTypeToTableType(contentType: PermContentType): TableType | undefined {
	const mapping: Record<PermContentType, TableType | undefined> = {
		assistants: TableType.assistant,
		chats: TableType.chat,
		collections: TableType.collection,
		files: TableType.file,
		groups: TableType.group,
		mcps: TableType.mcp,
		models: TableType.model,
		presets: TableType.preset,
		prompts: TableType.prompt,
		providers: TableType.provider,
		tenants: TableType.tenant,
		tools: TableType.tool,
		roles: undefined,
		workspaces: TableType.workspace,
		// special cases (map them to appropriate TableType or keep as undefined)
		assistantCollections: undefined,
		assistantFiles: undefined,
		assistantTools: undefined,
		config: undefined, // handles multiple categories
		collectionFiles: undefined,
		groupUsers: undefined,
		groupRoles: undefined,
		profiles: TableType.profile,
		sharing: undefined,
		tenantUsers: undefined,
	}

	return mapping[contentType]
}

export const authCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions): Promise<boolean> => {
			//type: PermContentType, action: PermAction, opContext?: string, uuid?: string
	authLogger.debug(`can perform, type:${options.type}, action:${PermAction[options.action]}, context:${options.context}, uuid:${options.uuid}, relUuid:${options.relUuid}`, {userId: authuser.email})
	const fetchedFunction = fetchCanPerformFunctions[options.type]
	if (fetchedFunction) {
		 const canPerformResult = await fetchedFunction(supabase, authuser, options)
		 void authLogger.log(canPerformResult ? 'debug' : 'info',
		 		`RESULT: can perform:${canPerformResult}, type:${options.type}, action:${PermAction[options.action]}, context:${options.context}, uuid:${options.uuid}, relUuid:${options.relUuid}`, {userId: authuser.email})
		 return canPerformResult
	}
	else {
		authLogger.error("No fetch function located to check permissions")
		return false
	}
}

export const evaluatePermissions = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: BatchPermOptions): Promise<Permissions> => {
	authLogger.debug(`evaluate permissions, type:${options.type}, context:${options.context}, uuid:${options.uuid}, relUuid:${options.relUuid}`, {userId: authuser.email})
	const fetchedFunction = fetchCanPerformFunctions[options.type]
	if (fetchedFunction) {
		const result = {} as Permissions
  	for (const action of Object.values(PermAction)) {
			const actionOptions = { ...options, action }
    	result[permOptionsKey(actionOptions)] = await fetchedFunction(supabase, authuser, actionOptions)
  	}
  	return result
	}
	else {
		authLogger.error("No fetch function located to evaluate permissions")
		const result = {} as Permissions
  	for (const action of Object.values(PermAction)) {
    	result[permOptionsKey({ ...options, action })] = false
  	}
  	return result
	}
}

export default Authuser
